import React, { FC, useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import keyBy from 'lodash/keyBy';

import { ARCHIVE_EXPORT } from '@common/constants/batchReportsConstants';

import { jobsSelector } from '../../shared/jim/store/selectors';
import { usePrevious } from '../../shared/hooks/usePrevious';
import { Job } from './types';
import { inProgressStatuses, statuses } from '../../shared/jim/constants';
import { jimJobConfigsByType } from '../../config';
import { errorNotification, successNotification } from '../../shared/store/notificationActions';
import { JobConfig } from '../../shared/jim/propTypes';
import { DownloadFileLink, NavigateToPageLink, ShowErrorsLink } from './NotificationLinks.component';

type JobsNotificationsProps = {
  jobs: Job[],
}

const inProgress = (job: Job) => inProgressStatuses.includes(job.status);

const JobsNotifications: FC<JobsNotificationsProps> = ({
  jobs,
}: JobsNotificationsProps) => {
  const dispatch = useDispatch();

  const previousJobs = usePrevious(jobs);
  const previousJobsById = useMemo(() => keyBy(previousJobs, 'jobId'), [previousJobs]);
  const location = useLocation();

  useEffect(() => {
    jobs.forEach(job => {
      const { jobId, status: currentStatus, jobType, returnedError } = job;

      if (!previousJobsById[jobId] || !inProgress(previousJobsById[jobId])) {
        return;
      }

      const jobConfig = jimJobConfigsByType[jobType] as JobConfig;

      if (!jobConfig) {
        return;
      }

      if (currentStatus === statuses.Failed && jimJobConfigsByType[jobType].errorMessage) {
        dispatch(errorNotification(jobConfig.errorMessage, {
          component: (<ShowErrorsLink returnedError={returnedError} />),
          timeOut: 6000,
        }));
      }

      if (currentStatus === statuses.Success && jobConfig.successMessage) {
        if (jobConfig.returnsFile) {
          dispatch(successNotification(jobConfig.successMessage, {
            component: (<DownloadFileLink job={job} isZipFile={job.jobType === ARCHIVE_EXPORT} />),
            timeOut: 6000,
          }));
          return;
        }

        if (jobConfig.page && location.pathname !== jobConfig.page) {
          dispatch(successNotification(jobConfig.successMessage, {
            component: (<NavigateToPageLink jobConfig={jobConfig} />),
            timeOut: 6000,
          }));
          return;
        }

        dispatch(successNotification(jobConfig.successMessage));
      }
    });
  }, [jobs, previousJobsById, dispatch, location.pathname]);

  return null;
};

export default connect(state => ({
  jobs: jobsSelector(state),
}))(JobsNotifications);
